// changed by Cursor: report note modal component - text box only
import React, { useState } from 'react';
import { Flag, X, CircleNotch, Check } from 'phosphor-react';
import { AnimatePresence, motion } from 'framer-motion';
import { reportNote } from '../../../services/reportApi';

const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 768;

const ReportNoteModal = ({ noteId, isOpen, onClose, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const canSubmit = reason.trim().length > 0 && !isSubmitting && submitStatus !== 'success';

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      const result = await reportNote(noteId, reason.trim());
      if (result.success) {
        setSubmitStatus('success');
        if (typeof onSuccess === 'function') {
          onSuccess(result.data);
        }
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        setSubmitStatus('error');
        setErrorMessage(result.message || 'Gagal mengirim laporan');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage(error.message || 'Gagal mengirim laporan');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setSubmitStatus(null);
    setErrorMessage('');
    onClose();
  };

  const modalVariants = isMobile()
    ? { hidden: { y: '100%' }, visible: { y: 0 }, exit: { y: '100%' } }
    : { hidden: { opacity: 0, scale: 0.95, y: 8 }, visible: { opacity: 1, scale: 1, y: 0 }, exit: { opacity: 0, scale: 0.95, y: 8 } };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="report-note-overlay fixed inset-0 bg-black/50 flex items-center justify-center z-[12000] p-4 max-md:items-end max-md:p-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          onClick={handleClose}
        >
          <motion.div
            className="report-note-modal bg-white dark:bg-[var(--bg-secondary)] rounded-[14px] w-full max-w-[420px] shadow-[0_20px_60px_rgba(0,0,0,0.15)] dark:shadow-[0_20px_60px_rgba(0,0,0,0.4)] overflow-hidden max-md:max-w-full max-md:rounded-[14px_14px_0_0]"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.2, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
          >
        <div className="flex items-center justify-between p-[16px_20px] border-b border-[#e5e7eb] dark:border-[var(--border-color)]">
          <div className="flex items-center gap-2 text-[15px] font-semibold text-[#0f172a] dark:text-[var(--text-primary)]">
            <Flag size={18} weight="bold" />
            <span>Laporkan Catatan</span>
          </div>
          <button
            className="flex items-center justify-center w-8 h-8 border-none bg-transparent rounded-[8px] cursor-pointer text-[#6b7280] transition-all duration-[150ms] hover:bg-[#f3f4f6] hover:text-[#1f2937] dark:hover:bg-[var(--bg-tertiary)] dark:hover:text-[var(--text-primary)]"
            onClick={handleClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5">
          <p className="text-[13px] font-medium text-[#374151] dark:text-[var(--text-secondary)] m-0 mb-3">Tulis alasan laporan:</p>
          <textarea
            className="w-full p-[10px_14px] border border-[#e5e7eb] dark:border-[var(--border-color)] rounded-[10px] text-[13px] font-[inherit] text-[#1f2937] dark:text-[var(--text-primary)] bg-white dark:bg-[var(--bg-tertiary)] resize-y min-h-[72px] transition-[border-color] duration-[150ms] box-border outline-none focus:border-[#0f766e] focus:shadow-[0_0_0_3px_rgba(15,118,110,0.1)] dark:focus:border-[var(--accent-teal)] dark:focus:shadow-[0_0_0_3px_rgba(20,184,166,0.15)]"
            placeholder="Contoh: penjelasan kurang jelas..."
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setSubmitStatus(null);
              setErrorMessage('');
            }}
            rows={3}
            maxLength={500}
            autoFocus
          />

          {submitStatus === 'error' && (
            <p className="mt-3 p-[10px_12px] text-[12px] text-[#dc2626] dark:text-[#fca5a5] bg-[#fef2f2] dark:bg-[rgba(220,38,38,0.1)] border border-[#fecaca] dark:border-[rgba(248,113,113,0.25)] rounded-[8px] m-0" role="alert" aria-live="polite">
              {errorMessage}
            </p>
          )}

          {submitStatus === 'success' && (
            <div className="flex items-center gap-[6px] mt-3 p-[8px_12px] text-[12px] text-[#16a34a] dark:text-[#4ade80] bg-[#f0fdf4] dark:bg-[rgba(34,197,94,0.1)] rounded-[8px]">
              <Check size={16} weight="bold" />
              <span>Laporan berhasil dikirim</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-[10px] p-[16px_20px] border-t border-[#e5e7eb] dark:border-[var(--border-color)] max-md:flex-col max-md:gap-2 max-md:pb-[calc(72px+env(safe-area-inset-bottom,0px))]">
          <button
            type="button"
            className="px-4 py-2 max-md:w-full max-md:py-3 max-md:rounded-[10px] max-md:text-[14px] border border-[#e5e7eb] dark:border-[var(--border-color)] rounded-[8px] bg-transparent text-[13px] text-[#6b7280] dark:text-[var(--text-secondary)] cursor-pointer transition-all duration-[150ms] hover:bg-[#f3f4f6] hover:text-[#374151] dark:hover:bg-[var(--bg-tertiary)] dark:hover:text-[var(--text-primary)] disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Batal
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center gap-[6px] px-[18px] py-2 max-md:w-full max-md:py-[14px] max-md:rounded-[10px] max-md:text-[15px] border-none rounded-[8px] !bg-[#0f766e] text-[13px] font-medium !text-white cursor-pointer transition-all duration-[150ms] hover:!bg-[#115e59] disabled:opacity-50 disabled:cursor-not-allowed [&_span]:!text-inherit"
            style={{ cursor: 'pointer' }}
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {isSubmitting ? (
              <>
                <CircleNotch size={14} weight="bold" className="shrink-0 animate-spin" />
                <span>Mengirim...</span>
              </>
            ) : (
              <span style={{ cursor: 'pointer' }}>Kirim Laporan</span>
            )}
          </button>
        </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReportNoteModal;
